/**
 * Vehicle autocomplete data
 * Common makes and models with proper formatting for parts catalogs
 */

export interface VehicleMake {
  name: string;
  models: string[];
}

/**
 * Common vehicle makes
 */
export const VEHICLE_MAKES = [
  'Acura',
  'Alfa Romeo',
  'Aston Martin',
  'Audi',
  'Bentley',
  'BMW',
  'Buick',
  'Cadillac',
  'Chevrolet',
  'Chrysler',
  'Dodge',
  'Ferrari',
  'Fiat',
  'Ford',
  'Genesis',
  'GMC',
  'Honda',
  'Hummer',
  'Hyundai',
  'Infiniti',
  'Isuzu',
  'Jaguar',
  'Jeep',
  'Kia',
  'Lamborghini',
  'Land Rover',
  'Lexus',
  'Lincoln',
  'Maserati',
  'Mazda',
  'Mercedes-Benz',
  'Mercury',
  'Mini',
  'Mitsubishi',
  'Nissan',
  'Oldsmobile',
  'Pontiac',
  'Porsche',
  'Ram',
  'Rivian',
  'Saab',
  'Saturn',
  'Scion',
  'Subaru',
  'Suzuki',
  'Tesla',
  'Toyota',
  'Volkswagen',
  'Volvo',
];

/**
 * Popular models by make with proper formatting
 */
export const VEHICLE_MODELS: Record<string, string[]> = {
  'Acura': [
    'CL',
    'ILX',
    'Integra',
    'Legend',
    'MDX',
    'NSX',
    'RDX',
    'RL',
    'RLX',
    'RSX',
    'TL',
    'TLX',
    'TSX',
    'Vigor',
    'ZDX',
  ],
  'Alfa Romeo': [
    '4C',
    'Giulia',
    'Stelvio',
    'Tonale',
  ],
  'Aston Martin': [
    'DB11',
    'DB9',
    'DBS',
    'Rapide',
    'Vantage',
  ],
  'Audi': [
    'A3',
    'A4',
    'A5',
    'A6',
    'A7',
    'A8',
    'e-tron',
    'Q3',
    'Q5',
    'Q7',
    'Q8',
    'R8',
    'RS3',
    'RS5',
    'RS6',
    'RS7',
    'S3',
    'S4',
    'S5',
    'S6',
    'S7',
    'S8',
    'SQ5',
    'SQ7',
    'SQ8',
    'TT',
  ],
  'Bentley': [
    'Bentayga',
    'Continental GT',
    'Flying Spur',
    'Mulsanne',
  ],
  'BMW': [
    '1 Series',
    '2 Series',
    '3 Series',
    '4 Series',
    '5 Series',
    '6 Series',
    '7 Series',
    '8 Series',
    'i3',
    'i4',
    'i7',
    'iX',
    'M2',
    'M3',
    'M4',
    'M5',
    'M8',
    'X1',
    'X2',
    'X3',
    'X4',
    'X5',
    'X6',
    'X7',
    'Z4',
  ],
  'Buick': [
    'Enclave',
    'Encore',
    'Encore GX',
    'Envision',
    'LaCrosse',
    'Regal',
  ],
  'Cadillac': [
    'ATS',
    'CT4',
    'CT5',
    'CT6',
    'CTS',
    'Escalade',
    'SRX',
    'XT4',
    'XT5',
    'XT6',
  ],
  'Chevrolet': [
    'Astro',
    'Avalanche',
    'Aveo',
    'Blazer',
    'Bolt',
    'Camaro',
    'Caprice',
    'Captiva',
    'Cavalier',
    'City Express',
    'Cobalt',
    'Colorado',
    'Corvette',
    'Cruze',
    'El Camino',
    'Equinox',
    'Express',
    'HHR',
    'Impala',
    'Lumina',
    'Malibu',
    'Monte Carlo',
    'S-10',
    'Silverado 1500',
    'Silverado 2500HD',
    'Silverado 3500HD',
    'Sonic',
    'Spark',
    'SS',
    'SSR',
    'Suburban',
    'Tahoe',
    'TrailBlazer',
    'Traverse',
    'Trax',
    'Uplander',
    'Venture',
    'Volt',
  ],
  'Chrysler': [
    '200',
    '300',
    'Pacifica',
    'Town & Country',
    'Voyager',
  ],
  'Dodge': [
    'Avenger',
    'Caliber',
    'Caravan',
    'Challenger',
    'Charger',
    'Dakota',
    'Dart',
    'Durango',
    'Grand Caravan',
    'Intrepid',
    'Journey',
    'Magnum',
    'Neon',
    'Nitro',
    'Ram 1500',
    'Ram 2500',
    'Ram 3500',
    'Stratus',
    'Viper',
  ],
  'Ferrari': [
    '360',
    '458',
    '488',
    'California',
    'F430',
    'Roma',
    'SF90',
  ],
  'Fiat': [
    '124 Spider',
    '500',
    '500L',
    '500X',
  ],
  'Ford': [
    'Bronco',
    'Bronco Sport',
    'C-Max',
    'Contour',
    'Crown Victoria',
    'E-150',
    'E-250',
    'E-350',
    'EcoSport',
    'Edge',
    'Escape',
    'Excursion',
    'Expedition',
    'Explorer',
    'Explorer Sport Trac',
    'F-150',
    'F-250',
    'F-350',
    'F-450',
    'Fiesta',
    'Five Hundred',
    'Flex',
    'Focus',
    'Freestar',
    'Freestyle',
    'Fusion',
    'Lightning',
    'Maverick',
    'Mustang',
    'Mustang Mach-E',
    'Ranger',
    'Taurus',
    'Thunderbird',
    'Transit',
    'Transit Connect',
    'Windstar',
  ],
  'GMC': [
    'Acadia',
    'Canyon',
    'Envoy',
    'Jimmy',
    'Safari',
    'Savana',
    'Sierra 1500',
    'Sierra 2500HD',
    'Sierra 3500HD',
    'Sonoma',
    'Terrain',
    'Yukon',
    'Yukon XL',
  ],
  'Genesis': [
    'G70',
    'G80',
    'G90',
    'GV60',
    'GV70',
    'GV80',
  ],
  'Honda': [
    'Accord',
    'Civic',
    'Clarity',
    'CR-V',
    'CR-Z',
    'Crosstour',
    'Element',
    'Fit',
    'HR-V',
    'Insight',
    'Odyssey',
    'Passport',
    'Pilot',
    'Prelude',
    'Ridgeline',
    'S2000',
  ],
  'Hyundai': [
    'Accent',
    'Azera',
    'Elantra',
    'Entourage',
    'Equus',
    'Genesis',
    'Genesis Coupe',
    'Ioniq',
    'Ioniq 5',
    'Ioniq 6',
    'Kona',
    'Nexo',
    'Palisade',
    'Santa Cruz',
    'Santa Fe',
    'Sonata',
    'Tiburon',
    'Tucson',
    'Veloster',
    'Venue',
    'Veracruz',
    'XG350',
  ],
  'Hummer': [
    'H1',
    'H2',
    'H3',
    'EV',
  ],
  'Infiniti': [
    'EX35',
    'EX37',
    'FX35',
    'FX37',
    'FX45',
    'FX50',
    'G25',
    'G35',
    'G37',
    'I30',
    'I35',
    'J30',
    'JX35',
    'M35',
    'M37',
    'M45',
    'M56',
    'Q40',
    'Q45',
    'Q50',
    'Q60',
    'Q70',
    'QX4',
    'QX30',
    'QX50',
    'QX55',
    'QX56',
    'QX60',
    'QX70',
    'QX80',
  ],
  'Isuzu': [
    'Amigo',
    'Ascender',
    'Axiom',
    'Hombre',
    'i-280',
    'i-290',
    'i-350',
    'i-370',
    'Rodeo',
    'Trooper',
    'VehiCROSS',
  ],
  'Jaguar': [
    'E-PACE',
    'F-PACE',
    'F-TYPE',
    'I-PACE',
    'S-Type',
    'X-Type',
    'XE',
    'XF',
    'XJ',
    'XK',
  ],
  'Jeep': [
    'Cherokee',
    'Commander',
    'Compass',
    'Gladiator',
    'Grand Cherokee',
    'Grand Cherokee L',
    'Grand Wagoneer',
    'Liberty',
    'Patriot',
    'Renegade',
    'Wagoneer',
    'Wrangler',
    'Wrangler Unlimited',
  ],
  'Kia': [
    'Amanti',
    'Borrego',
    'Cadenza',
    'Carnival',
    'EV6',
    'Forte',
    'K5',
    'K900',
    'Niro',
    'Optima',
    'Rio',
    'Rondo',
    'Sedona',
    'Seltos',
    'Sorento',
    'Soul',
    'Spectra',
    'Sportage',
    'Stinger',
    'Telluride',
  ],
  'Lamborghini': [
    'Aventador',
    'Gallardo',
    'Huracan',
    'Urus',
  ],
  'Land Rover': [
    'Defender',
    'Discovery',
    'Discovery Sport',
    'Freelander',
    'LR2',
    'LR3',
    'LR4',
    'Range Rover',
    'Range Rover Evoque',
    'Range Rover Sport',
    'Range Rover Velar',
  ],
  'Lexus': [
    'ES',
    'GS',
    'GX',
    'IS',
    'LS',
    'LX',
    'NX',
    'RC',
    'RX',
    'UX',
  ],
  'Lincoln': [
    'Aviator',
    'Continental',
    'Corsair',
    'MKC',
    'MKT',
    'MKX',
    'MKZ',
    'Nautilus',
    'Navigator',
  ],
  'Maserati': [
    'Ghibli',
    'GranSport',
    'GranTurismo',
    'Grecale',
    'Levante',
    'MC20',
    'Quattroporte',
  ],
  'Mazda': [
    'CX-3',
    'CX-30',
    'CX-5',
    'CX-50',
    'CX-9',
    'CX-90',
    'Mazda3',
    'Mazda6',
    'MX-5 Miata',
    'MX-30',
    'RX-7',
    'RX-8',
    'Tribute',
  ],
  'Mercedes-Benz': [
    'A-Class',
    'AMG GT',
    'B-Class',
    'C-Class',
    'CL-Class',
    'CLA',
    'CLK-Class',
    'CLS',
    'E-Class',
    'EQB',
    'EQC',
    'EQE',
    'EQS',
    'G-Class',
    'GL-Class',
    'GLA',
    'GLB',
    'GLC',
    'GLE',
    'GLK-Class',
    'GLS',
    'M-Class',
    'Metris',
    'R-Class',
    'S-Class',
    'SL-Class',
    'SLK-Class',
    'SLS AMG',
    'Sprinter',
  ],
  'Mercury': [
    'Cougar',
    'Grand Marquis',
    'Mariner',
    'Milan',
    'Montego',
    'Monterey',
    'Mountaineer',
    'Sable',
    'Villager',
  ],
  'Mini': [
    'Clubman',
    'Convertible',
    'Cooper',
    'Cooper S',
    'Countryman',
    'Hardtop',
    'Paceman',
  ],
  'Mitsubishi': [
    'Eclipse',
    'Eclipse Cross',
    'Endeavor',
    'Galant',
    'Lancer',
    'Mirage',
    'Montero',
    'Montero Sport',
    'Outlander',
    'Outlander Sport',
    'Raider',
  ],
  'Nissan': [
    '240SX',
    '300ZX',
    '350Z',
    '370Z',
    'Altima',
    'Armada',
    'Cube',
    'Frontier',
    'GT-R',
    'Juke',
    'Kicks',
    'Leaf',
    'Maxima',
    'Murano',
    'NV200',
    'Pathfinder',
    'Quest',
    'Rogue',
    'Rogue Sport',
    'Sentra',
    'Titan',
    'Titan XD',
    'Versa',
    'Xterra',
  ],
  'Oldsmobile': [
    'Alero',
    'Aurora',
    'Bravada',
    'Cutlass',
    'Intrigue',
    'Silhouette',
  ],
  'Pontiac': [
    'Aztek',
    'Bonneville',
    'Firebird',
    'G5',
    'G6',
    'G8',
    'Grand Am',
    'Grand Prix',
    'GTO',
    'Montana',
    'Solstice',
    'Sunfire',
    'Torrent',
    'Vibe',
  ],
  'Porsche': [
    '718 Boxster',
    '718 Cayman',
    '911',
    'Boxster',
    'Cayenne',
    'Cayman',
    'Macan',
    'Panamera',
    'Taycan',
  ],
  'Ram': [
    '1500',
    '2500',
    '3500',
    'ProMaster',
    'ProMaster City',
  ],
  'Rivian': [
    'R1S',
    'R1T',
  ],
  'Saab': [
    '9-2X',
    '9-3',
    '9-5',
    '9-7X',
  ],
  'Saturn': [
    'Astra',
    'Aura',
    'Ion',
    'L-Series',
    'Outlook',
    'Relay',
    'S-Series',
    'Sky',
    'Vue',
  ],
  'Scion': [
    'FR-S',
    'iA',
    'iM',
    'iQ',
    'tC',
    'xA',
    'xB',
    'xD',
  ],
  'Subaru': [
    'Ascent',
    'Baja',
    'BRZ',
    'Crosstrek',
    'Forester',
    'Impreza',
    'Legacy',
    'Outback',
    'Solterra',
    'SVX',
    'Tribeca',
    'WRX',
    'XV Crosstrek',
  ],
  'Suzuki': [
    'Aerio',
    'Equator',
    'Esteem',
    'Forenza',
    'Grand Vitara',
    'Kizashi',
    'Reno',
    'Samurai',
    'Sidekick',
    'SX4',
    'Swift',
    'Verona',
    'Vitara',
    'XL7',
  ],
  'Tesla': [
    'Model 3',
    'Model S',
    'Model X',
    'Model Y',
  ],
  'Toyota': [
    '4Runner',
    '86',
    'Avalon',
    'bZ4X',
    'C-HR',
    'Camry',
    'Celica',
    'Corolla',
    'Corolla Cross',
    'Corona',
    'Cressida',
    'Echo',
    'FJ Cruiser',
    'GR86',
    'GR Corolla',
    'GR Supra',
    'Highlander',
    'Land Cruiser',
    'Matrix',
    'MR2',
    'Prius',
    'Prius Prime',
    'RAV4',
    'RAV4 Prime',
    'Sequoia',
    'Sienna',
    'Solara',
    'Supra',
    'T100',
    'Tacoma',
    'Tercel',
    'Tundra',
    'Venza',
    'Yaris',
  ],
  'Volkswagen': [
    'Arteon',
    'Atlas',
    'Atlas Cross Sport',
    'Beetle',
    'CC',
    'e-Golf',
    'Eos',
    'Golf',
    'Golf R',
    'GTI',
    'ID.4',
    'Jetta',
    'Passat',
    'Phaeton',
    'Rabbit',
    'Routan',
    'Taos',
    'Tiguan',
    'Touareg',
  ],
  'Volvo': [
    'C30',
    'C40',
    'C70',
    'S40',
    'S60',
    'S80',
    'S90',
    'V40',
    'V60',
    'V90',
    'XC40',
    'XC60',
    'XC70',
    'XC90',
  ],
};

/**
 * Get model suggestions for a specific make
 */
export const getModelSuggestions = (make: string): string[] => {
  return VEHICLE_MODELS[make] || [];
};

/**
 * Get all available makes
 */
export const getMakeSuggestions = (): string[] => {
  return VEHICLE_MAKES;
};
